import React, { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';

import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import BackButton from '@/components/ui/back-button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

// Define schema for the test email form
const testEmailFormSchema = z.object({
  email: z.string().email({ message: 'Please enter a valid email address' }),
  name: z.string().min(1, { message: 'Name is required' }),
});

type TestEmailFormValues = z.infer<typeof testEmailFormSchema>;

// Define a simple email schema just for the email address
const simpleEmailSchema = z.object({
  to: z.string().email({ message: 'Please enter a valid email address' }),
});

type SimpleEmailValues = z.infer<typeof simpleEmailSchema>;

export default function SendTestEmailPage() {
  const { toast } = useToast();
  const [result, setResult] = useState<any>(null);
  const [simpleResult, setSimpleResult] = useState<any>(null);
  const [directResult, setDirectResult] = useState<any>(null);
  const [sendgridDirectResult, setSendgridDirectResult] = useState<any>(null);

  // Form definition for LOA email
  const form = useForm<TestEmailFormValues>({
    resolver: zodResolver(testEmailFormSchema),
    defaultValues: {
      email: 'fs@domelec.co.uk',
      name: 'Francis Smith',
    },
  });

  // Form definition for simple email (tracking disabled)
  const simpleForm = useForm<SimpleEmailValues>({
    resolver: zodResolver(simpleEmailSchema),
    defaultValues: {
      to: 'fs@domelec.co.uk',
    },
  });

  // Form definition for direct email (tracking enabled)
  const directForm = useForm<SimpleEmailValues>({
    resolver: zodResolver(simpleEmailSchema),
    defaultValues: {
      to: 'fs@domelec.co.uk',
    },
  });

  // Form definition for direct SendGrid API email
  const sgDirectForm = useForm<SimpleEmailValues>({
    resolver: zodResolver(simpleEmailSchema),
    defaultValues: {
      to: 'fs@domelec.co.uk',
    },
  });

  // Send complex LOA test email mutation
  const sendEmailMutation = useMutation({
    mutationFn: async (data: TestEmailFormValues) => {
      return apiRequest('POST', '/api/public/test/send-loa-email', data);
    },
    onSuccess: (data) => {
      toast({
        title: 'Email Sent Successfully',
        description: `Test email was sent to ${form.getValues().email}`,
      });
      setResult({
        success: true,
        data,
      });
    },
    onError: (error: Error) => {
      console.error('Failed to send test email:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to Send Email',
        description: error.message || 'There was an error sending the test email.',
      });
      setResult({
        success: false,
        error: error.message,
      });
    },
  });

  // Send simple test email mutation (no tracking)
  const sendSimpleEmailMutation = useMutation({
    mutationFn: async (data: SimpleEmailValues) => {
      return apiRequest('POST', '/api/test/simple-email', data);
    },
    onSuccess: (data) => {
      toast({
        title: 'Simple Email Sent Successfully',
        description: `Test email was sent to ${simpleForm.getValues().to}`,
      });
      setSimpleResult({
        success: true,
        data,
      });
    },
    onError: (error: Error) => {
      console.error('Failed to send simple test email:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to Send Simple Email',
        description: error.message || 'There was an error sending the simple test email.',
      });
      setSimpleResult({
        success: false,
        error: error.message,
      });
    },
  });

  // Send direct test email mutation (with tracking enabled)
  const sendDirectEmailMutation = useMutation({
    mutationFn: async (data: SimpleEmailValues) => {
      return apiRequest('POST', '/api/test/direct-email', data);
    },
    onSuccess: (data) => {
      toast({
        title: 'Direct Email Sent Successfully',
        description: `Test email with tracking was sent to ${directForm.getValues().to}`,
      });
      setDirectResult({
        success: true,
        data,
      });
    },
    onError: (error: Error) => {
      console.error('Failed to send direct test email:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to Send Direct Email',
        description: error.message || 'There was an error sending the direct test email.',
      });
      setDirectResult({
        success: false,
        error: error.message,
      });
    },
  });

  // Send email using direct SendGrid API mutation
  const sendSendgridDirectMutation = useMutation({
    mutationFn: async (data: SimpleEmailValues) => {
      return apiRequest('POST', '/api/test/sendgrid-direct', data);
    },
    onSuccess: (data) => {
      toast({
        title: 'SendGrid Direct API Email Sent',
        description: `Test email via direct API was sent to ${sgDirectForm.getValues().to}`,
      });
      setSendgridDirectResult({
        success: true,
        data,
      });
    },
    onError: (error: Error) => {
      console.error('Failed to send SendGrid direct API email:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to Send via SendGrid API',
        description: error.message || 'There was an error sending via direct SendGrid API.',
      });
      setSendgridDirectResult({
        success: false,
        error: error.message,
      });
    },
  });

  // Handle form submission for LOA email
  function onSubmit(data: TestEmailFormValues) {
    setResult(null);
    sendEmailMutation.mutate(data);
  }

  // Handle form submission for simple email
  function onSubmitSimpleEmail(data: SimpleEmailValues) {
    setSimpleResult(null);
    sendSimpleEmailMutation.mutate(data);
  }

  // Handle form submission for direct email with tracking
  function onSubmitDirectEmail(data: SimpleEmailValues) {
    setDirectResult(null);
    sendDirectEmailMutation.mutate(data);
  }

  // Handle form submission for SendGrid direct API email
  function onSubmitSendgridDirect(data: SimpleEmailValues) {
    setSendgridDirectResult(null);
    sendSendgridDirectMutation.mutate(data);
  }

  // Define interface for SendGrid verification response
  interface VerificationResults {
    apiKeyValid: boolean;
    accountActive: boolean;
    senderVerified: boolean;
    domainAuthenticated: boolean;
    emailPermissionsCorrect: boolean;
    profile: any;
    verifiedSenders: any[];
    domains: any[];
    emailRateLimits: any;
    issues: string[];
    recommendations: string[];
  }

  interface VerifyResponse {
    success: boolean;
    message: string;
    status?: number;
    data?: any;
    error?: string;
    verificationError?: string;
    profile?: any;
    verifiedSenders?: any[];
    results?: VerificationResults;
    recommendations?: string[];
    diagnosticInfo?: {
      apiKey: string;
      fromEmail: string;
      environment: Record<string, string>;
    };
  }

  // Query to fetch SendGrid account verification status
  const {
    data: verifyData,
    isLoading: isVerifyLoading,
    isError: isVerifyError,
    error: verifyError,
    refetch: refetchVerify,
  } = useQuery<VerifyResponse>({
    queryKey: ['/api/test/verify-sendgrid'],
    enabled: false, // Don't load automatically on page load
  });

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="flex items-center mb-6 flex-wrap">
        <BackButton to="/" label="Back to Dashboard" />
        <h1 className="text-2xl font-bold ml-4 mt-2 sm:mt-0">Send Test Email</h1>
      </div>

      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle>Test Email Functionality</CardTitle>
          <CardDescription>
            Send test emails to verify the email sending functionality is working correctly.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="loa">
            <TabsList className="mb-4 w-full flex flex-wrap">
              <TabsTrigger value="loa" className="flex-1 min-w-[100px]">
                LOA Email
              </TabsTrigger>
              <TabsTrigger value="simple" className="flex-1 min-w-[100px]">
                No-Tracking
              </TabsTrigger>
              <TabsTrigger value="direct" className="flex-1 min-w-[100px]">
                With-Tracking
              </TabsTrigger>
              <TabsTrigger value="sg-api" className="flex-1 min-w-[100px]">
                Direct API
              </TabsTrigger>
              <TabsTrigger value="verify-sg" className="flex-1 min-w-[100px]">
                Verify SendGrid
              </TabsTrigger>
            </TabsList>

            <TabsContent value="loa">
              <div className="mb-4">
                <Alert>
                  <AlertTitle>Letter of Authority Email</AlertTitle>
                  <AlertDescription>
                    This will send a full Letter of Authority (LOA) template email with attachments
                    to the specified address.
                  </AlertDescription>
                </Alert>
              </div>

              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email Address</FormLabel>
                        <FormControl>
                          <Input placeholder="email@example.com" {...field} />
                        </FormControl>
                        <FormDescription>
                          Enter the email address to send the test message to.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Recipient Name</FormLabel>
                        <FormControl>
                          <Input placeholder="John Doe" {...field} />
                        </FormControl>
                        <FormDescription>
                          The name will be used in the email greeting.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button type="submit" disabled={sendEmailMutation.isPending} className="w-full">
                    {sendEmailMutation.isPending ? 'Sending...' : 'Send Test LOA Email'}
                  </Button>
                </form>
              </Form>

              {result && (
                <div className="mt-6">
                  {result.success ? (
                    <Alert className="bg-green-50 border-green-200">
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                      <AlertTitle className="text-green-800">Email Sent Successfully</AlertTitle>
                      <AlertDescription className="text-green-700">
                        <p className="mt-2">The test email was sent successfully.</p>
                        {result.data?.signingUrl && (
                          <div className="mt-2">
                            <p className="font-semibold">Signing URL:</p>
                            <a
                              href={result.data.signingUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 underline break-all"
                            >
                              {result.data.signingUrl}
                            </a>
                          </div>
                        )}
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <Alert variant="destructive">
                      <AlertCircle className="h-5 w-5" />
                      <AlertTitle>Email Sending Failed</AlertTitle>
                      <AlertDescription>
                        <p className="mt-2">There was an error sending the email:</p>
                        <pre className="bg-red-50 p-2 mt-2 rounded text-sm overflow-auto">
                          {typeof result.error === 'object'
                            ? JSON.stringify(result.error, null, 2)
                            : result.error}
                        </pre>
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}
            </TabsContent>

            <TabsContent value="simple">
              <div className="mb-4">
                <Alert>
                  <AlertTitle>No-Tracking Email Test</AlertTitle>
                  <AlertDescription>
                    This sends a basic email with all SendGrid tracking features disabled. Used to
                    diagnose delivery issues when click tracking might be interfering with links.
                  </AlertDescription>
                </Alert>
              </div>

              <Form {...simpleForm}>
                <form onSubmit={simpleForm.handleSubmit(onSubmitSimpleEmail)} className="space-y-6">
                  <FormField
                    control={simpleForm.control}
                    name="to"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email Address</FormLabel>
                        <FormControl>
                          <Input placeholder="email@example.com" {...field} />
                        </FormControl>
                        <FormDescription>
                          Enter the email address to send the simple test message to.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    disabled={sendSimpleEmailMutation.isPending}
                    className="w-full"
                  >
                    {sendSimpleEmailMutation.isPending ? 'Sending...' : 'Send Simple Test Email'}
                  </Button>
                </form>
              </Form>

              {simpleResult && (
                <div className="mt-6">
                  {simpleResult.success ? (
                    <Alert className="bg-green-50 border-green-200">
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                      <AlertTitle className="text-green-800">
                        Simple Email Sent Successfully
                      </AlertTitle>
                      <AlertDescription className="text-green-700">
                        <p className="mt-2">The simple test email was sent successfully.</p>
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <Alert variant="destructive">
                      <AlertCircle className="h-5 w-5" />
                      <AlertTitle>Simple Email Sending Failed</AlertTitle>
                      <AlertDescription>
                        <p className="mt-2">There was an error sending the simple email:</p>
                        <pre className="bg-red-50 p-2 mt-2 rounded text-sm overflow-auto">
                          {typeof simpleResult.error === 'object'
                            ? JSON.stringify(simpleResult.error, null, 2)
                            : simpleResult.error}
                        </pre>
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}
            </TabsContent>

            <TabsContent value="direct">
              <div className="mb-4">
                <Alert>
                  <AlertTitle>With-Tracking Email Test</AlertTitle>
                  <AlertDescription>
                    This sends a test email with SendGrid's tracking features enabled. Use this to
                    compare delivery times and behavior with tracking turned on.
                  </AlertDescription>
                </Alert>
              </div>

              <Form {...directForm}>
                <form onSubmit={directForm.handleSubmit(onSubmitDirectEmail)} className="space-y-6">
                  <FormField
                    control={directForm.control}
                    name="to"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email Address</FormLabel>
                        <FormControl>
                          <Input placeholder="email@example.com" {...field} />
                        </FormControl>
                        <FormDescription>
                          Enter the email address to send the tracked test message to.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    disabled={sendDirectEmailMutation.isPending}
                    className="w-full"
                  >
                    {sendDirectEmailMutation.isPending ? 'Sending...' : 'Send Email With Tracking'}
                  </Button>
                </form>
              </Form>

              {directResult && (
                <div className="mt-6">
                  {directResult.success ? (
                    <Alert className="bg-green-50 border-green-200">
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                      <AlertTitle className="text-green-800">
                        Tracked Email Sent Successfully
                      </AlertTitle>
                      <AlertDescription className="text-green-700">
                        <p className="mt-2">The test email with tracking was sent successfully.</p>
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <Alert variant="destructive">
                      <AlertCircle className="h-5 w-5" />
                      <AlertTitle>Tracked Email Sending Failed</AlertTitle>
                      <AlertDescription>
                        <p className="mt-2">There was an error sending the email with tracking:</p>
                        <pre className="bg-red-50 p-2 mt-2 rounded text-sm overflow-auto">
                          {typeof directResult.error === 'object'
                            ? JSON.stringify(directResult.error, null, 2)
                            : directResult.error}
                        </pre>
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}
            </TabsContent>

            <TabsContent value="sg-api">
              <div className="mb-4">
                <Alert>
                  <AlertTitle>Direct SendGrid API Test</AlertTitle>
                  <AlertDescription>
                    This makes a direct API call to SendGrid's v3 mail/send endpoint, bypassing the
                    standard client library. Use this as a last resort when other methods aren't
                    working.
                  </AlertDescription>
                </Alert>
              </div>

              <Form {...sgDirectForm}>
                <form
                  onSubmit={sgDirectForm.handleSubmit(onSubmitSendgridDirect)}
                  className="space-y-6"
                >
                  <FormField
                    control={sgDirectForm.control}
                    name="to"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email Address</FormLabel>
                        <FormControl>
                          <Input placeholder="email@example.com" {...field} />
                        </FormControl>
                        <FormDescription>
                          Enter the email address to send the direct API test message to.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    disabled={sendSendgridDirectMutation.isPending}
                    variant="destructive"
                    className="w-full"
                  >
                    {sendSendgridDirectMutation.isPending
                      ? 'Sending...'
                      : 'Send via Direct SendGrid API'}
                  </Button>
                </form>
              </Form>

              {sendgridDirectResult && (
                <div className="mt-6">
                  {sendgridDirectResult.success ? (
                    <Alert className="bg-green-50 border-green-200">
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                      <AlertTitle className="text-green-800">
                        Direct API Email Sent Successfully
                      </AlertTitle>
                      <AlertDescription className="text-green-700">
                        <p className="mt-2">
                          The test email via direct SendGrid API was sent successfully.
                        </p>
                        <p className="mt-2">Status: {sendgridDirectResult.data?.status}</p>
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <Alert variant="destructive">
                      <AlertCircle className="h-5 w-5" />
                      <AlertTitle>Direct API Email Sending Failed</AlertTitle>
                      <AlertDescription>
                        <p className="mt-2">There was an error sending the email via direct API:</p>
                        <pre className="bg-red-50 p-2 mt-2 rounded text-sm overflow-auto">
                          {typeof sendgridDirectResult.error === 'object'
                            ? JSON.stringify(sendgridDirectResult.error, null, 2)
                            : sendgridDirectResult.error}
                        </pre>
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}
            </TabsContent>

            <TabsContent value="verify-sg">
              <div className="mb-4">
                <Alert>
                  <AlertTitle>SendGrid Account Verification</AlertTitle>
                  <AlertDescription>
                    This tool checks your SendGrid account configuration to help diagnose email
                    delivery issues. It will check API key validity, domain authentication status,
                    and sender verification.
                  </AlertDescription>
                </Alert>
              </div>

              <div className="flex justify-center my-6">
                <Button
                  onClick={() => refetchVerify()}
                  disabled={isVerifyLoading}
                  className="min-w-[200px]"
                >
                  {isVerifyLoading ? 'Verifying...' : 'Verify SendGrid Account'}
                </Button>
              </div>

              {isVerifyLoading && (
                <div className="my-8 flex justify-center items-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                </div>
              )}

              {verifyData && (
                <div className="mt-6 border rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-4">SendGrid Account Status</h3>

                  <div className="mb-4">
                    <div className="flex items-center">
                      <div
                        className={`mr-2 rounded-full w-3 h-3 ${verifyData.success ? 'bg-green-500' : 'bg-red-500'}`}
                      ></div>
                      <span className="font-medium">Overall Status:</span>
                      <span className="ml-2">
                        {verifyData.success
                          ? 'Configuration appears valid'
                          : 'Configuration has issues'}
                      </span>
                    </div>
                  </div>

                  {verifyData.message && (
                    <div className="mb-4">
                      <span className="font-medium">Summary:</span>
                      <p className="mt-1">{verifyData.message}</p>
                    </div>
                  )}

                  {/* Display the diagnostic checklist */}
                  {verifyData.results && (
                    <div className="mb-6 border rounded p-3 bg-gray-50">
                      <h4 className="font-medium mb-2">Configuration Checklist:</h4>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div className="flex items-center">
                          <div
                            className={`mr-2 rounded-full w-3 h-3 ${verifyData.results.apiKeyValid ? 'bg-green-500' : 'bg-red-500'}`}
                          ></div>
                          <span>
                            API Key Valid: {verifyData.results.apiKeyValid ? 'Yes' : 'No'}
                          </span>
                        </div>

                        <div className="flex items-center">
                          <div
                            className={`mr-2 rounded-full w-3 h-3 ${verifyData.results.accountActive ? 'bg-green-500' : 'bg-red-500'}`}
                          ></div>
                          <span>
                            Account Active: {verifyData.results.accountActive ? 'Yes' : 'No'}
                          </span>
                        </div>

                        <div className="flex items-center">
                          <div
                            className={`mr-2 rounded-full w-3 h-3 ${verifyData.results.senderVerified ? 'bg-green-500' : 'bg-amber-500'}`}
                          ></div>
                          <span>
                            Sender Verified: {verifyData.results.senderVerified ? 'Yes' : 'No'}
                          </span>
                        </div>

                        <div className="flex items-center">
                          <div
                            className={`mr-2 rounded-full w-3 h-3 ${verifyData.results.domainAuthenticated ? 'bg-green-500' : 'bg-amber-500'}`}
                          ></div>
                          <span>
                            Domain Authenticated:{' '}
                            {verifyData.results.domainAuthenticated ? 'Yes' : 'No'}
                          </span>
                        </div>

                        <div className="flex items-center">
                          <div
                            className={`mr-2 rounded-full w-3 h-3 ${verifyData.results.emailPermissionsCorrect ? 'bg-green-500' : 'bg-red-500'}`}
                          ></div>
                          <span>
                            Email Permissions:{' '}
                            {verifyData.results.emailPermissionsCorrect ? 'Valid' : 'Invalid'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Display identified issues */}
                  {verifyData.results &&
                    verifyData.results.issues &&
                    verifyData.results.issues.length > 0 && (
                      <div className="mb-4">
                        <h4 className="font-medium mb-2 text-amber-600">Identified Issues:</h4>
                        <ul className="list-disc ml-5 space-y-1 text-amber-600">
                          {verifyData.results.issues.map((issue, idx) => (
                            <li key={idx}>{issue}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                  {/* Display recommendations */}
                  {((verifyData.results &&
                    verifyData.results.recommendations &&
                    verifyData.results.recommendations.length > 0) ||
                    verifyData.recommendations) && (
                    <div className="mb-6 p-3 border rounded border-blue-200 bg-blue-50">
                      <h4 className="font-medium mb-2 text-blue-700">Recommendations:</h4>
                      <ul className="list-disc ml-5 space-y-1 text-blue-700">
                        {verifyData.results &&
                          verifyData.results.recommendations &&
                          verifyData.results.recommendations.map((rec, idx) => (
                            <li key={idx}>{rec}</li>
                          ))}
                        {verifyData.recommendations &&
                          verifyData.recommendations.map((rec, idx) => <li key={idx}>{rec}</li>)}
                      </ul>
                    </div>
                  )}

                  {/* Diagnostic Information (collapsible) */}
                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="diagnostic-info">
                      <AccordionTrigger className="text-sm">
                        Show Detailed Diagnostic Information
                      </AccordionTrigger>
                      <AccordionContent>
                        {verifyData.diagnosticInfo && (
                          <div className="mb-4">
                            <h4 className="font-medium mb-1">Environment Configuration:</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                              <div>
                                <span className="font-mono">API Key:</span>{' '}
                                {verifyData.diagnosticInfo.apiKey}
                              </div>
                              <div>
                                <span className="font-mono">From Email:</span>{' '}
                                {verifyData.diagnosticInfo.fromEmail}
                              </div>
                              {Object.entries(verifyData.diagnosticInfo.environment || {}).map(
                                ([key, value]) => (
                                  <div key={key}>
                                    <span className="font-mono">{key}:</span> {value as string}
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        )}

                        {verifyData.profile && (
                          <div className="mb-4">
                            <h4 className="font-medium mb-1">Account Profile:</h4>
                            <pre className="bg-gray-50 p-3 rounded text-xs overflow-auto">
                              {JSON.stringify(verifyData.profile, null, 2)}
                            </pre>
                          </div>
                        )}

                        {verifyData.results && verifyData.results.verifiedSenders && (
                          <div className="mb-4">
                            <h4 className="font-medium mb-1">Verified Senders:</h4>
                            <pre className="bg-gray-50 p-3 rounded text-xs overflow-auto">
                              {JSON.stringify(verifyData.results.verifiedSenders, null, 2)}
                            </pre>
                          </div>
                        )}

                        {verifyData.results && verifyData.results.domains && (
                          <div className="mb-4">
                            <h4 className="font-medium mb-1">Authenticated Domains:</h4>
                            <pre className="bg-gray-50 p-3 rounded text-xs overflow-auto">
                              {JSON.stringify(verifyData.results.domains, null, 2)}
                            </pre>
                          </div>
                        )}

                        {verifyData.status && (
                          <div className="mb-4">
                            <span className="font-medium">API Response Status:</span>
                            <p className="mt-1">{verifyData.status}</p>
                          </div>
                        )}

                        {verifyData.data && (
                          <div>
                            <h4 className="font-medium mb-1">Raw Response Data:</h4>
                            <pre className="bg-gray-50 p-3 rounded text-xs overflow-auto">
                              {JSON.stringify(verifyData.data, null, 2)}
                            </pre>
                          </div>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>

                  {verifyData.error && (
                    <div className="mt-4">
                      <h4 className="font-medium mb-1 text-red-600">Error:</h4>
                      <p className="text-red-600 mb-1">{verifyData.error}</p>
                    </div>
                  )}
                </div>
              )}

              {isVerifyError && verifyError && (
                <Alert variant="destructive" className="mt-6">
                  <AlertCircle className="h-5 w-5" />
                  <AlertTitle>Verification Failed</AlertTitle>
                  <AlertDescription>
                    <p className="mt-2">There was an error verifying the SendGrid account:</p>
                    <pre className="bg-red-50 p-2 mt-2 rounded text-sm overflow-auto">
                      {verifyError instanceof Error
                        ? verifyError.message
                        : JSON.stringify(verifyError)}
                    </pre>
                  </AlertDescription>
                </Alert>
              )}

              <div className="mt-8 text-sm text-gray-600">
                <h4 className="font-medium mb-2">Common Issues:</h4>
                <ul className="list-disc ml-5 space-y-1">
                  <li>SendGrid API key is invalid or has expired</li>
                  <li>Domain authentication is not properly set up (missing SPF/DKIM records)</li>
                  <li>Sender identity is not verified</li>
                  <li>Account is in a trial state with sending limitations</li>
                  <li>Account has been flagged for suspicious activity</li>
                </ul>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
        <CardFooter className="flex justify-between text-sm text-gray-500">
          <p>This page is for testing purposes only.</p>
        </CardFooter>
      </Card>
    </div>
  );
}
